// AGENT-A6: Two-pill segmented control with an implicit "either/null" state.
//
// Click the active pill to clear back to null. Click the other pill to flip.
// Selected pill: --dash-accent bg + white text.
// Unselected pill: white bg + --dash-fg text, 1px --dash-hairline border.
// Hover (unselected): --dash-accent-soft bg.

import { useCallback } from "react";

export interface BooleanToggleProps {
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  labels: [string, string];
  /** Tiny header label rendered above the control. */
  header?: string;
  /** Accessible label override for the group. */
  ariaLabel?: string;
}

export function BooleanToggle(props: BooleanToggleProps): JSX.Element {
  const { value, onChange, labels, header, ariaLabel } = props;

  const handle = useCallback(
    (next: boolean) => {
      if (value === next) {
        onChange(null);
        return;
      }
      onChange(next);
    },
    [onChange, value],
  );

  return (
    <div className="dash-filter-field">
      {header ? <span className="dash-filter-field__header">{header}</span> : null}
      <div
        className="dash-segmented"
        role="group"
        aria-label={ariaLabel ?? header ?? "toggle"}
      >
        <button
          type="button"
          className={`dash-segmented__pill${
            value === true ? " dash-segmented__pill--active" : ""
          }`}
          onClick={() => handle(true)}
          aria-pressed={value === true}
        >
          {labels[0]}
        </button>
        <button
          type="button"
          className={`dash-segmented__pill${
            value === false ? " dash-segmented__pill--active" : ""
          }`}
          onClick={() => handle(false)}
          aria-pressed={value === false}
        >
          {labels[1]}
        </button>
      </div>
    </div>
  );
}
